import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { useGameSignalR } from '../hooks/useSignalR';
import { gameService } from '../services/gameService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { toast } from 'sonner';

export const Lobby = () => {
  const [roomCode, setRoomCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const { players, setRoomCode: setStoreRoomCode } = useGameStore();
  const { joinRoom, markReady, startGame } = useGameSignalR();
  const navigate = useNavigate();

  const handleCreateRoom = async () => {
    setIsCreating(true);
    try {
      const { roomCode: newRoomCode } = await gameService.createRoom({});
      setStoreRoomCode(newRoomCode);
      await joinRoom(newRoomCode);
      toast.success(`Room created: ${newRoomCode}`);
    } catch (error) {
      toast.error('Failed to create room');
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinRoom = async () => {
    if (!roomCode.trim()) {
      toast.error('Please enter a room code');
      return;
    }
    setIsJoining(true);
    try {
      await joinRoom(roomCode);
      setStoreRoomCode(roomCode);
      toast.success('Joined room successfully');
    } catch (error) {
      toast.error('Failed to join room');
    } finally {
      setIsJoining(false);
    }
  };

  const handleReady = async () => {
    await markReady();
  };

  const handleStartGame = async () => {
    await startGame();
    navigate(`/game/${roomCode || useGameStore.getState().roomCode}`);
  };

  const currentRoomCode = useGameStore.getState().roomCode;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-primary">غرفة الانتظار</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!currentRoomCode ? (
            <>
              <Button
                onClick={handleCreateRoom}
                disabled={isCreating}
                className="w-full bg-secondary hover:bg-secondary-dark text-white"
                size="lg"
              >
                {isCreating ? <LoadingSpinner size="sm" /> : 'إنشاء غرفة'}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">OR</span>
                </div>
              </div>

              <div className="space-y-2">
                <Input
                  placeholder="Enter room code"
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleJoinRoom()}
                />
                <Button
                  onClick={handleJoinRoom}
                  disabled={isJoining}
                  variant="outline"
                  className="w-full border-2 border-primary text-primary hover:bg-red-50"
                >
                  {isJoining ? <LoadingSpinner size="sm" /> : 'الانضمام لغرفة'}
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600">رمز الغرفة</p>
                <p className="text-2xl font-bold text-primary">{currentRoomCode}</p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-primary">اللاعبون ({players.length})</h3>
                {players.map((player) => (
                  <div
                    key={player.userId}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                  >
                    <span className="font-medium">{player.username}</span>
                    {player.isReady && <span className="text-accent-green font-semibold">✓ جاهز</span>}
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleReady} 
                  variant="outline" 
                  className="flex-1 border-2 border-primary text-primary hover:bg-red-50"
                >
                  جاهز
                </Button>
                <Button 
                  onClick={handleStartGame} 
                  className="flex-1 bg-secondary hover:bg-secondary-dark text-white" 
                  size="lg"
                >
                  ابدأ اللعبة
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
